import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Link,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { Disclosure } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { QRCodeSVG } from "qrcode.react";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { organizationStore, useOrganizationStore } from "../../store/organization";
import { eventStore, useEventStore } from "../../store/event";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import toast from "react-hot-toast";
import { GET_ORDER_AHEAD_OVERVIEW, GET_EVENT_LANDING_SLUGS } from "./queries";
import {
  UPDATE_EVENT_ORDER_AHEAD_SLUG,
  UPDATE_EVENT_CHECKOUT_FIELD_LABEL,
} from "./mutations";
import { UPDATE_ORDER_AHEAD_ITEM } from "../tickets/order-ahead-items/mutations";
import { usePermissions } from "../auth/api";
import { canManageOrderAhead, canViewOrderAheadOrders } from "./access";
import { userStore } from "../../store/user";
import { OrderAheadItem, OrderAheadLocation } from "../../types/orderAhead";
import { ORDER_AHEAD_HOST, menuOrderUrl, downloadQrPng } from "./qrDownload";

const client = createApolloClient();
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;
const { getState: getUserState } = userStore;

interface OverviewData {
  menus: OrderAheadLocation[];
  items: OrderAheadItem[];
  eventId: number | null;
  eventName: string;
}

export const orderAheadOverviewLoader = async (
  _args: LoaderFunctionArgs
): Promise<OverviewData> => {
  const rawOrganizationId = getOrganizationState().organizationId;
  const organizationId = parseInt(
    rawOrganizationId !== null ? rawOrganizationId.toString() : "",
    10
  );
  const eventState = getEventState();
  const eventId = eventState.eventId;

  // The page is event-scoped — without a selected event there is nothing to show.
  if (!eventId) {
    return { menus: [], items: [], eventId: null, eventName: "" };
  }

  let locWhere: any = { event_id: { _eq: eventId } };
  let itemWhere: any = { event_id: { _eq: eventId } };
  if (organizationId && organizationId !== 0) {
    const orgFilter = { event: { organization_id: { _eq: organizationId } } };
    locWhere = { ...locWhere, ...orgFilter };
    itemWhere = { ...itemWhere, ...orgFilter };
  }

  const { data } = await client.query({
    query: GET_ORDER_AHEAD_OVERVIEW,
    variables: { locWhere, itemWhere },
  });

  const menus: OrderAheadLocation[] = data.locations_order_ahead ?? [];
  const storeName = eventState.allEvents?.find(
    (e: any) => e.id === eventId
  )?.name;

  return {
    menus,
    items: data.items_order_ahead ?? [],
    eventId,
    eventName: menus[0]?.event?.name ?? storeName ?? "",
  };
};

interface CategoryGroup {
  name: string;
  hidden: boolean;
  items: OrderAheadItem[];
  // Raw ids from the category JSON that didn't resolve to an item in this
  // event — deleted items, or references left behind by a cross-event copy.
  unresolvedIds: string[];
}

// A menu's items live in its `category` JSON as cat_1..cat_6 with cat_N_itemsIds
// arrays of item-id strings. Resolve those ids against the event's item list.
const resolveCategories = (
  menu: OrderAheadLocation,
  itemsById: Map<number, OrderAheadItem>
): CategoryGroup[] => {
  const category: any = menu.category;
  const groups: CategoryGroup[] = [];
  if (!category || typeof category !== "object") return groups;

  for (let i = 1; i <= 6; i++) {
    const name = category[`cat_${i}`];
    if (!name) continue;
    const ids: any[] = category[`cat_${i}_itemsIds`] ?? [];
    const items: OrderAheadItem[] = [];
    const unresolvedIds: string[] = [];
    for (const id of ids) {
      const item = itemsById.get(Number(id));
      if (item) items.push(item);
      else unresolvedIds.push(String(id));
    }
    groups.push({
      name,
      hidden: Boolean(category[`cat_${i}_hide`]),
      items,
      unresolvedIds,
    });
  }
  return groups;
};

const formatPrice = (cents: number) => `$${((cents ?? 0) / 100).toFixed(2)}`;

// tax_percentage is stored as a fraction (0.075 = 7.5%); shown here the same way
// the item form displays it.
const formatTax = (item: OrderAheadItem) => {
  if (item.tax_percentage == null && !item.tax_type) return "No tax set";
  const pct = Math.round((item.tax_percentage ?? 0) * 100 * 100) / 100;
  const type =
    item.tax_type === "inclusive"
      ? "Inclusive"
      : item.tax_type === "exclusive"
        ? "Exclusive"
        : "";
  return type ? `${pct}% ${type}` : `${pct}%`;
};

type ItemSortKey = "menu" | "name" | "category" | "vendor" | "tax" | "price";

// One row of a menu's item table. menuOrder preserves the configured category /
// item ordering so the default sort matches what customers see.
interface ItemRow {
  item: OrderAheadItem;
  category: string;
  categoryHidden: boolean;
  menuOrder: number;
}

const compareRows = (a: ItemRow, b: ItemRow, key: ItemSortKey): number => {
  switch (key) {
    case "name":
      return a.item.name.localeCompare(b.item.name);
    case "category":
      return a.category.localeCompare(b.category) || a.menuOrder - b.menuOrder;
    case "vendor":
      return (a.item.vendor?.name ?? "").localeCompare(b.item.vendor?.name ?? "");
    case "tax":
      return (a.item.tax_percentage ?? 0) - (b.item.tax_percentage ?? 0);
    case "price":
      return (a.item.price ?? 0) - (b.item.price ?? 0);
    default:
      return a.menuOrder - b.menuOrder;
  }
};

const SortableTh = ({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: ItemSortKey;
  sort: { key: ItemSortKey; dir: 1 | -1 };
  onSort: (key: ItemSortKey) => void;
  align?: "left" | "right";
}) => (
  <th
    className={`py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}
  >
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-700 ${
        sort.key === sortKey ? "text-gray-700" : ""
      }`}
    >
      {label}
      {sort.key === sortKey && (
        <span className="text-[10px]">{sort.dir === 1 ? "▲" : "▼"}</span>
      )}
    </button>
  </th>
);

const StatusPill = ({
  label,
  tone,
}: {
  label: string;
  tone: "green" | "gray" | "blue" | "amber";
}) => {
  const tones = {
    green: "bg-green-100 text-green-800",
    gray: "bg-gray-100 text-gray-600",
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-amber-100 text-amber-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {label}
    </span>
  );
};

const MenuCard = ({
  menu,
  itemsById,
}: {
  menu: OrderAheadLocation;
  itemsById: Map<number, OrderAheadItem>;
}) => {
  const navigate = useNavigate();
  const { user } = usePermissions();
  const canManage = canManageOrderAhead(user);
  const qrRef = useRef<HTMLDivElement>(null);
  const categories = useMemo(
    () => resolveCategories(menu, itemsById),
    [menu, itemsById]
  );
  const itemCount = categories.reduce((sum, c) => sum + c.items.length, 0);

  // Setup problems that make this menu partly or fully unsellable, shown even
  // when the item list is collapsed.
  const warnings = useMemo(() => {
    const list: string[] = [];
    if (!menu.url_guid) {
      list.push(
        "No ordering URL — this menu has no customer-facing page or QR code."
      );
    }
    if (!menu.payment_processor_config) {
      list.push("No payment processor — customers can't check out on this menu.");
    }
    const broken = categories.filter((c) => c.unresolvedIds.length > 0);
    if (broken.length > 0) {
      const count = broken.reduce((sum, c) => sum + c.unresolvedIds.length, 0);
      const detail = broken
        .map((c) => `${c.name}: ${c.unresolvedIds.map((id) => `#${id}`).join(", ")}`)
        .join("; ");
      list.push(
        `${count} item reference${count === 1 ? "" : "s"} ${
          count === 1 ? "doesn't" : "don't"
        } resolve to an item in this event (deleted, or copied from another event) — ${detail}`
      );
    }
    return list;
  }, [menu, categories]);

  const [sort, setSort] = useState<{ key: ItemSortKey; dir: 1 | -1 }>({
    key: "menu",
    dir: 1,
  });
  const handleSort = (key: ItemSortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

  const rows = useMemo(() => {
    const flat: ItemRow[] = [];
    for (const cat of categories) {
      for (const item of cat.items) {
        flat.push({
          item,
          category: cat.name,
          categoryHidden: cat.hidden,
          menuOrder: flat.length,
        });
      }
    }
    return flat.sort((a, b) => compareRows(a, b, sort.key) * sort.dir);
  }, [categories, sort]);

  return (
    <div className="shadow sm:rounded-md bg-white px-4 py-4 mb-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            {canManage ? (
              <Link
                to={`/order-ahead-locations/${menu.id}/edit`}
                className="font-semibold text-gray-900 hover:text-primary-600 hover:underline"
              >
                {menu.name}
              </Link>
            ) : (
              <span className="font-semibold text-gray-900">{menu.name}</span>
            )}
            {menu.is_active ? (
              <StatusPill label="Active" tone="green" />
            ) : (
              <StatusPill label="Inactive" tone="gray" />
            )}
            {menu.is_pickup && <StatusPill label="Pickup" tone="blue" />}
          </div>
          <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
            <div>
              <dt className="inline font-medium text-gray-400">Location: </dt>
              <dd className="inline">{menu.zone || "No zone set"}</dd>
            </div>
            <div>
              <dt className="inline font-medium text-gray-400">Pickup: </dt>
              <dd className="inline">
                {menu.is_pickup
                  ? menu.pickup_note || "On (no note)"
                  : "Off"}
              </dd>
            </div>
            <div>
              <dt className="inline font-medium text-gray-400">Service fee: </dt>
              <dd className="inline">
                {menu.digital_surcharge_percentage ?? 0}%
              </dd>
            </div>
            <div>
              <dt className="inline font-medium text-gray-400">Tips: </dt>
              <dd className="inline">{menu.is_tips ? "On" : "Off"}</dd>
            </div>
            <div>
              <dt className="inline font-medium text-gray-400">Processor: </dt>
              <dd className="inline">
                {menu.payment_processor_config?.payment_processor || "—"}
              </dd>
            </div>
          </dl>
          {warnings.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {warnings.map((w) => (
                <li key={w} className="text-xs font-medium text-amber-700">
                  ⚠ {w}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {menu.url_guid && (
            <SecondaryButton
              onClick={() =>
                downloadQrPng(qrRef, `order-ahead-qr-${menu.url_guid}.png`)
              }
            >
              Download QR
            </SecondaryButton>
          )}
          {canManage && (
            <SecondaryButton
              onClick={() => navigate(`/order-ahead-locations/${menu.id}/edit`)}
            >
              Edit Menu
            </SecondaryButton>
          )}
        </div>
      </div>

      {/* Off-screen QR for this menu's ordering page — rasterized on demand by Download QR. */}
      {menu.url_guid && (
        <div ref={qrRef} className="hidden">
          <QRCodeSVG value={menuOrderUrl(menu.url_guid)} size={200} includeMargin />
        </div>
      )}

      <Disclosure>
        {({ open }) => (
          <div className="mt-3 border-t border-gray-100 pt-2">
            <Disclosure.Button className="flex items-center text-sm font-medium text-primary-600">
              <ChevronRightIcon
                className={`h-4 w-4 transition-transform duration-200 ${
                  open ? "rotate-90" : ""
                }`}
              />
              Items ({itemCount})
            </Disclosure.Button>
            <Disclosure.Panel static>
              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                  open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="mt-2 pl-1">
              {rows.length === 0 ? (
                <p className="text-sm text-gray-400">
                  No items assigned to this menu.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs text-gray-400">
                      <SortableTh label="Item" sortKey="name" sort={sort} onSort={handleSort} />
                      <SortableTh label="Category" sortKey="category" sort={sort} onSort={handleSort} />
                      <SortableTh label="Vendor" sortKey="vendor" sort={sort} onSort={handleSort} />
                      <SortableTh label="Tax" sortKey="tax" sort={sort} onSort={handleSort} align="right" />
                      <SortableTh label="Price" sortKey="price" sort={sort} onSort={handleSort} align="right" />
                      <th className="w-12" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((row) => {
                      const vendorMismatch =
                        row.item.vendor_id !== menu.vendor_id;
                      return (
                        <tr key={`${row.category}-${row.item.id}`}>
                          <td className="py-1.5 text-gray-700">
                            {row.item.name}
                            {!row.item.is_active && (
                              <span className="ml-2 text-xs text-gray-400">
                                (inactive)
                              </span>
                            )}
                            {row.item.available_public_sale === false && (
                              <span
                                className="ml-2 text-xs font-medium text-amber-600"
                                title="available_public_sale is off — not orderable on the public order-ahead site"
                              >
                                (not public)
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 text-gray-400">
                            {row.category}
                            {row.categoryHidden && " (hidden)"}
                          </td>
                          <td
                            className={`py-1.5 ${
                              vendorMismatch
                                ? "font-medium text-amber-700"
                                : "text-gray-500"
                            }`}
                            title={
                              vendorMismatch
                                ? `Differs from the menu's vendor (${menu.vendor?.name ?? "unknown"})`
                                : undefined
                            }
                          >
                            {row.item.vendor?.name ?? "—"}
                            {vendorMismatch && " ≠"}
                          </td>
                          <td className="py-1.5 text-right text-gray-400">
                            {formatTax(row.item)}
                          </td>
                          <td className="py-1.5 text-right text-gray-500">
                            {formatPrice(row.item.price)}
                          </td>
                          <td className="py-1.5 text-right">
                            {canManage && (
                              <Link
                                to={`/order-ahead-items/${row.item.id}/edit`}
                                className="font-medium text-primary-600 hover:text-primary-700"
                              >
                                Edit
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
                  </div>
                </div>
              </div>
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    </div>
  );
};

// Animated collapsible section used to group the event-level config editors on one card.
// Uses the grid-rows 0fr→1fr technique so it animates to the content's natural height.
const CollapsibleSection = ({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
      >
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <ChevronRightIcon
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
            open ? "rotate-90" : ""
          }`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4">{children}</div>
        </div>
      </div>
    </div>
  );
};

// Event-level editor for the order-ahead landing slug. Loads/saves on its own so a
// missing order_ahead_slug column can't break the rest of the overview page.
const EventLandingSlug = ({ eventId }: { eventId: number }) => {
  const [slug, setSlug] = useState("");
  const [savedSlug, setSavedSlug] = useState("");
  const [cashlessSlug, setCashlessSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    client
      .query({
        query: GET_EVENT_LANDING_SLUGS,
        variables: { eventId },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (cancelled) return;
        const ev = data?.events?.[0];
        const oaSlug = ev?.configuration?.order_ahead_slug ?? "";
        setSlug(oaSlug);
        setSavedSlug(oaSlug);
        setCashlessSlug(ev?.slug ?? "");
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const trimmed = slug.trim();
  const dirty = trimmed !== savedSlug.trim();

  const handleSave = async () => {
    setSaving(true);
    try {
      const value = trimmed === "" ? null : trimmed;
      await client.mutate({
        mutation: UPDATE_EVENT_ORDER_AHEAD_SLUG,
        variables: { eventId, patch: { order_ahead_slug: value } },
      });
      setSavedSlug(value ?? "");
      setSlug(value ?? "");
      toast.success("Order-ahead landing slug saved");
    } catch {
      toast.error("Couldn't save the slug — it may already be in use.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loadError ? (
        <p className="mt-3 text-sm text-red-600">
          Couldn't load the slug for this event. Try again in a moment.
        </p>
      ) : (
        <>
          <div className="mt-3 flex items-center">
            <input
              id="order-ahead-slug"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="e.g. summer-fest-2026"
              disabled={loading || saving}
              className="block w-full max-w-md rounded-md border-gray-300 shadow-sm sm:text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
            />
            <PrimaryButton
              onClick={handleSave}
              isDisabled={!dirty || loading || saving}
              isLoading={saving}
            >
              Save
            </PrimaryButton>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            {trimmed ? (
              <>
                Landing page:{" "}
                <span className="font-mono">
                  {ORDER_AHEAD_HOST}/order/event/{trimmed}
                </span>
              </>
            ) : (
              "No slug set — this event's order-ahead landing page won't resolve until you set one."
            )}
          </p>
          {cashlessSlug && (
            <p className="mt-1 text-xs text-gray-400">
              Cashless slug:{" "}
              <span className="font-mono">{cashlessSlug}</span>
            </p>
          )}
          {savedSlug.trim() && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-900">
                Order-ahead QR code
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Customers who scan this are sent to the landing page above.
              </p>
              <div
                ref={qrRef}
                className="mt-2 flex w-fit justify-start rounded border border-gray-200 bg-white p-4"
              >
                <QRCodeSVG
                  value={`https://${ORDER_AHEAD_HOST}/order/event/${savedSlug.trim()}`}
                  size={200}
                  includeMargin
                />
              </div>
              <div className="mt-2">
                <SecondaryButton
                  onClick={() =>
                    downloadQrPng(
                      qrRef,
                      `order-ahead-qr-${savedSlug.trim()}.png`
                    )
                  }
                >
                  Download PNG
                </SecondaryButton>
              </div>
              {dirty && (
                <p className="mt-2 text-xs text-amber-600">
                  Showing the QR for the saved slug. Save your changes to update
                  it.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

// Event-level editor for the order-ahead checkout field label (e.g. "Table
// number", "Campsite number"). The customer-facing checkout shows a required
// numeric input with this label on table-service (non-pickup) menus; leaving it
// blank hides the field entirely. Loads/saves on its own, mirroring
// EventLandingSlug, so it can't break the rest of the overview page.
const CheckoutFieldLabel = ({ eventId }: { eventId: number }) => {
  const [label, setLabel] = useState("");
  const [savedLabel, setSavedLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    client
      .query({
        query: GET_EVENT_LANDING_SLUGS,
        variables: { eventId },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (cancelled) return;
        const ev = data?.events?.[0];
        const value = ev?.configuration?.checkout_field_label ?? "";
        setLabel(value);
        setSavedLabel(value);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const trimmed = label.trim();
  const dirty = trimmed !== savedLabel.trim();

  const handleSave = async () => {
    setSaving(true);
    try {
      const value = trimmed === "" ? null : trimmed;
      await client.mutate({
        mutation: UPDATE_EVENT_CHECKOUT_FIELD_LABEL,
        variables: { eventId, patch: { checkout_field_label: value } },
      });
      setSavedLabel(value ?? "");
      setLabel(value ?? "");
      toast.success("Checkout field label saved");
    } catch {
      toast.error("Couldn't save the checkout field label.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {loadError ? (
        <p className="mt-3 text-sm text-red-600">
          Couldn't load the checkout field label for this event. Try again in a
          moment.
        </p>
      ) : (
        <>
          <div className="mt-3 flex items-center">
            <input
              id="checkout-field-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Table number, Campsite number"
              disabled={loading || saving}
              className="block w-full max-w-md rounded-md border-gray-300 shadow-sm sm:text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
            />
            <PrimaryButton
              onClick={handleSave}
              isDisabled={!dirty || loading || saving}
              isLoading={saving}
            >
              Save
            </PrimaryButton>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            {trimmed
              ? `Customers ordering from table-service (non-pickup) menus will be required to enter a "${trimmed}" at checkout.`
              : "No label set — the checkout field is hidden for this event."}
          </p>
        </>
      )}
    </>
  );
};

// Single card grouping the event-level order-ahead config editors as animated
// collapsible sections.
const EventConfigCard = ({ eventId }: { eventId: number }) => (
  <div className="shadow sm:rounded-md bg-white mb-6 divide-y divide-gray-100">
    <CollapsibleSection title="Event QR code">
      <EventLandingSlug eventId={eventId} />
    </CollapsibleSection>
    <CollapsibleSection title="Checkout field label">
      <CheckoutFieldLabel eventId={eventId} />
    </CollapsibleSection>
  </div>
);

export const OrderAheadOverview = () => {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { menus, items, eventId, eventName } = useLoaderData() as OverviewData;
  const { user } = usePermissions();
  const canManage = canManageOrderAhead(user);
  const canViewOrders = canViewOrderAheadOrders(user);

  // The page's data comes from a route loader, which only re-runs on navigation.
  // Re-run it when the header's event / organization picker changes.
  const headerEventId = useEventStore((s) => s.eventId);
  const headerOrgId = useOrganizationStore((s) => s.organizationId);
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    revalidator.revalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerEventId, headerOrgId]);

  const itemsById = useMemo(() => {
    const map = new Map<number, OrderAheadItem>();
    for (const item of items) map.set(item.id, item);
    return map;
  }, [items]);

  const vendorGroups = useMemo(() => {
    const map = new Map<
      number,
      { vendorName: string; menus: OrderAheadLocation[] }
    >();
    for (const menu of menus) {
      const group = map.get(menu.vendor_id) ?? {
        vendorName: menu.vendor?.name ?? "Unknown vendor",
        menus: [],
      };
      group.menus.push(menu);
      map.set(menu.vendor_id, group);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.vendorName.localeCompare(b.vendorName)
    );
  }, [menus]);

  // Ids referenced by any menu's category JSON. Items outside this set (or
  // inactive ones) have no other UI surface, so they get their own section below.
  const assignedItemIds = useMemo(() => {
    const ids = new Set<number>();
    for (const menu of menus) {
      const category: any = menu.category;
      if (!category || typeof category !== "object") continue;
      for (const key of Object.keys(category)) {
        if (!/^cat_\d+_itemsIds$/.test(key)) continue;
        for (const id of category[key] ?? []) ids.add(Number(id));
      }
    }
    return ids;
  }, [menus]);

  const attentionItems = useMemo(
    () => items.filter((it) => !it.is_active || !assignedItemIds.has(it.id)),
    [items, assignedItemIds]
  );

  const [reactivatingId, setReactivatingId] = useState<number | null>(null);
  const handleReactivate = async (item: OrderAheadItem) => {
    setReactivatingId(item.id);
    try {
      await client.mutate({
        mutation: UPDATE_ORDER_AHEAD_ITEM,
        variables: {
          input: { is_active: true, last_updated_by: getUserState().user?.id },
          where: { id: { _eq: item.id } },
        },
      });
      toast.success(`${item.name} reactivated`);
      revalidator.revalidate();
    } catch {
      toast.error("Couldn't reactivate the item");
    } finally {
      setReactivatingId(null);
    }
  };

  return (
    <div className="px-2 pb-10">
      <div className="grid gap-2 grid-cols-6">
        <div className="col-span-3 mt-2">
          <Breadcrumbs />
        </div>
        <div className="col-span-3 mt-2 flex justify-end">
          {canViewOrders && (
            <SecondaryButton onClick={() => navigate("/order-ahead-orders/list")}>
              Search Orders
            </SecondaryButton>
          )}
          {canManage && (
            <>
              <SecondaryButton
                onClick={() => navigate("/order-ahead-items/create")}
              >
                New Item
              </SecondaryButton>
              <SecondaryButton
                onClick={() => navigate("/order-ahead-locations/list")}
              >
                New Location
              </SecondaryButton>
              <PrimaryButton
                onClick={() => navigate("/order-ahead-locations/create")}
              >
                New Menu
              </PrimaryButton>
            </>
          )}
        </div>
      </div>

      <h1 className="mt-4 mb-4 text-xl font-semibold text-gray-900">
        Order Ahead{eventName ? ` — ${eventName}` : ""}
      </h1>

      {!eventId && (
        <div className="shadow sm:rounded-md bg-white px-6 py-10 text-center">
          <p className="font-medium text-gray-700">Select an event</p>
          <p className="mt-1 text-sm text-gray-500">
            Use the event picker in the top bar to choose an event and see its
            order-ahead menus.
          </p>
        </div>
      )}

      {eventId && <EventConfigCard eventId={eventId} />}

      {eventId && vendorGroups.length === 0 && (
        <div className="shadow sm:rounded-md bg-white px-6 py-10 text-center">
          <p className="font-medium text-gray-700">
            No order-ahead menus for this event
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Create one with the New Menu button above.
          </p>
        </div>
      )}

      {eventId &&
        vendorGroups.map((group) => (
          <section key={group.vendorName} className="mb-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-800">
              {group.vendorName}
              <span className="ml-2 text-sm font-normal text-gray-400">
                ({group.menus.length} menu
                {group.menus.length === 1 ? "" : "s"})
              </span>
            </h2>
            {group.menus.map((menu) => (
              <MenuCard key={menu.id} menu={menu} itemsById={itemsById} />
            ))}
          </section>
        ))}

      {eventId && attentionItems.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-lg font-semibold text-gray-800">
            Inactive and unassigned items
            <span className="ml-2 text-sm font-normal text-gray-400">
              ({attentionItems.length})
            </span>
          </h2>
          <div className="shadow sm:rounded-md bg-white px-4 py-4">
            <p className="mb-2 text-sm text-gray-500">
              Items for this event that are inactive or not on any menu.
              Reactivate an item to make it orderable again, then add it to a
              menu from the menu's edit page if it isn't on one.
            </p>
            {attentionItems.map((item) => (
              <div
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-50 py-2 text-sm"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-gray-700">{item.name}</span>
                  {item.vendor?.name && (
                    <span className="text-gray-400">{item.vendor.name}</span>
                  )}
                  {!item.is_active && <StatusPill label="Inactive" tone="gray" />}
                  {!assignedItemIds.has(item.id) && (
                    <StatusPill label="Not on any menu" tone="blue" />
                  )}
                  {item.available_public_sale === false && (
                    <StatusPill label="Not public" tone="amber" />
                  )}
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-gray-400">{formatTax(item)}</span>
                  <span className="text-gray-500">{formatPrice(item.price)}</span>
                  {canManage && !item.is_active && (
                    <SecondaryButton
                      onClick={() => handleReactivate(item)}
                      isDisabled={reactivatingId === item.id}
                      isLoading={reactivatingId === item.id}
                    >
                      Reactivate
                    </SecondaryButton>
                  )}
                  {canManage && (
                    <Link
                      to={`/order-ahead-items/${item.id}/edit`}
                      className="font-medium text-primary-600 hover:text-primary-700"
                    >
                      Edit
                    </Link>
                  )}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
